import * as stylex from '@stylexjs/stylex';
import { styles } from './styles.ts';
import FreelanceEmployeeForm from '@entities/freelance-employee-form/index.tsx';
import AddSegment from '@features/add-segment/index.tsx';
import { IFreelanceEmployee } from '@shared/types/types.ts';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';
import './styles.css';
import CompaniesStructure from './companies-structure.tsx';
import { InvitedParticipants } from '@widgets/segments-tab';
import { ErrorData } from '@features/api/use-create-user';
import { formatPhoneNumber } from '@entities/create-segment-modal/lib/form-validator.ts';
import { FreelanceEmployeeStep } from './model/types';

interface IFreelanceEmployeeProps {
  freelanceEmployees: IFreelanceEmployee[];
  onChange: (freelanceEmployees: IFreelanceEmployee[]) => void;
  onEmailError: (hasError: boolean) => void;
  invitedParticipants?: InvitedParticipants[];
  guestSelectedParticipants: number[];
  setGuestSelectedParticipants: (ids: number[]) => void;
  setGuestParticipantsDelete?: Dispatch<SetStateAction<InvitedParticipants[]>>;
  guestParticipantsDelete?: InvitedParticipants[];
  setEditableEmployee?: Dispatch<SetStateAction<IFreelanceEmployee | undefined>>;
  editableEmployee?: IFreelanceEmployee;
  step?: FreelanceEmployeeStep;
  setStep?: (step: FreelanceEmployeeStep) => void;
  errors?: ErrorData[];
}

export const FreelanceEmployee: React.FC<IFreelanceEmployeeProps> = memo(
  ({
    freelanceEmployees = [],
    onChange,
    onEmailError,
    invitedParticipants,
    guestSelectedParticipants,
    setGuestSelectedParticipants,
    setGuestParticipantsDelete,
    guestParticipantsDelete,
    setEditableEmployee,
    editableEmployee,
    step,
    setStep,
    errors,
  }) => {
    const addEmployeeHandler = useCallback(() => {
      const newEmployee: IFreelanceEmployee = {
        id: Date.now(),
        lastName: '',
        firstName: '',
        email: '',
        phone: '',
        comment: '',
      };
      onChange([...freelanceEmployees, newEmployee]);
      setStep?.(FreelanceEmployeeStep.FORM);
    }, [freelanceEmployees, onChange]);

    const editEmployeeHandler = useCallback(
      (employee: IFreelanceEmployee) => {
        onChange([employee]);
        const phone = employee.phone.replace('+7', '').slice(-10);
        employee.phone = formatPhoneNumber(8 + phone);
        setEditableEmployee?.(employee);
        setStep?.(FreelanceEmployeeStep.FORM);
      },
      [freelanceEmployees, onChange]
    );

    const changeEployeeChangeHandler = useCallback(
      (employee: IFreelanceEmployee, id: number) => {
        onChange(freelanceEmployees.map((item, i) => (i === id ? employee : item)));
      },
      [freelanceEmployees, onChange]
    );

    const hasEmptyName = freelanceEmployees.some((employee) => employee.lastName.trim() === '');
    const hasEmptyMail = freelanceEmployees.some((employee) => employee.email.trim() === '');
    const hasEmptyPhone = freelanceEmployees.some((employee) => employee.phone.trim() === '');

    const conditionAdding = hasEmptyName || hasEmptyMail || hasEmptyPhone;

    return (
      <>
        {step === FreelanceEmployeeStep.FORM ? (
          <>
            <div className="custom-scroll">
              <div style={{ maxHeight: '400px' }}>
                {freelanceEmployees.map((employee, index) => (
                  <FreelanceEmployeeForm
                    key={index}
                    employee={employee}
                    changeEmployee={(employee) => changeEployeeChangeHandler(employee, index)}
                    onChangeDelete={onChange}
                    freelanceEmployees={freelanceEmployees}
                    onEmailError={onEmailError}
                    error={errors?.[0]}
                    deletable={!editableEmployee}
                  />
                ))}
              </div>
            </div>

            {conditionAdding && (
              <div {...stylex.props(styles.reminderFreeForm)}>Все обязательные поля должны быть заполнены!</div>
            )}

            {!editableEmployee && (
              <div {...stylex.props(styles.addSegmentBtn)}>
                <AddSegment text="Добавить участника" onClick={addEmployeeHandler} width="100%" />
              </div>
            )}
          </>
        ) : (
          <>
            <CompaniesStructure
              source="guest"
              showFilter={false}
              invitedParticipants={invitedParticipants || []}
              selectedParticipants={guestSelectedParticipants}
              setSelectedParticipants={setGuestSelectedParticipants}
              setParticipantsDelete={setGuestParticipantsDelete}
              participantsDelete={guestParticipantsDelete}
              setEditableEmployee={editEmployeeHandler}
            />

            <div {...stylex.props(styles.addSegmentBtn)}>
              <AddSegment text="Добавить участника" onClick={addEmployeeHandler} width="100%" />
            </div>
          </>
        )}
      </>
    );
  }
);
